import { ChangeEventHandler, useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import Collapse from '@/components/ui/collapse'
import { Input } from '@/components/ui/input'
import RenderIf from '@/components/ui/render-if'
import { Text } from '@/components/ui/text'
import SlippageWarning from '@/containers/home/settings/SlippageWarning'
import { useSlippageSettings } from '@/hooks/use-slippage'
import { cn } from '@/lib'
import { SLIPPAGE_WARNING } from '@/store/slippage'

interface SlippageToleranceProps {
  isOpen: boolean
  warning?: boolean
  realtime?: boolean // sync slippage to store realtime
  fixedSwap: boolean
  isBridge: boolean
}

const slippageOptions = [0.1, 0.5, 1] // %

const SlippageSetting = ({ isOpen, warning = true, realtime, fixedSwap, isBridge }: SlippageToleranceProps) => {
  const {
    slippage: slippageGlobal,
    setSlippage: setSlippageGlobal,
    maxSlippage,
  } = useSlippageSettings({ isFixedSwap: fixedSwap, isBridge })
  const [slippage, setSlippage] = useState<string | number>(slippageGlobal)

  useEffect(() => {
    if (isOpen) {
      setSlippage('')
    }
  }, [isOpen])

  const onChangeSlippage: ChangeEventHandler<HTMLInputElement> = (e) => {
    const rawValue = e.target.value
    const numVal = +rawValue
    const numberRegex = /^(\d+)\.?(\d{1,6})?$/
    if (
      rawValue &&
      (isNaN(numVal) || numVal > maxSlippage || numVal < 0 || !rawValue.match(numberRegex) || rawValue.startsWith('00'))
    ) {
      return
    }
    setSlippage(rawValue)
    if (realtime) setSlippageGlobal(numVal)
  }

  const showWarning = warning ? +slippage > SLIPPAGE_WARNING : false
  return (
    <Collapse expandClassName='mt-4' maxHeight={showWarning ? '120px' : '34px'} isExpanded={isOpen}>
      <div className='flex flex-col gap-6'>
        <div className='flex justify-between gap-4'>
          {slippageOptions.map((opt) => {
            return (
              <div className='flex-1' key={opt}>
                <Button
                  variant='secondary'
                  className={cn('w-full !text-size-16', {
                    'bg-main-4 text-main-100 ring-[1px] ring-main-100': opt === slippageGlobal,
                  })}
                  onClick={() => {
                    setSlippageGlobal(opt)
                    setSlippage('')
                  }}
                >
                  <Text
                    size={16}
                    className={cn({
                      'text-main-100': opt === slippageGlobal,
                    })}
                  >
                    {opt}%
                  </Text>
                </Button>
              </div>
            )
          })}
          <div className='flex-1'>
            <Input
              size='sm'
              value={slippage}
              onBlur={() => {
                if (realtime) return
                slippage && setSlippageGlobal(+slippage)
              }}
              onChange={onChangeSlippage}
              suffix='%'
              placeholder='Custom'
              className='w-full bg-transparent text-size-16 md:min-w-[114px] min-w-[100px]'
            />
          </div>
        </div>
        <RenderIf condition={showWarning}>
          <SlippageWarning />
        </RenderIf>
      </div>
    </Collapse>
  )
}

export default SlippageSetting
